import { ChangeDetectorRef, inject, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TRANSLATE_SERVICE } from '@luggagehero/shared/interfaces';
import { Subscription } from 'rxjs';

/**
 * Reactive translation pipe that updates when the language changes.
 * Uses the shared translation service which supports text variants.
 */
@Pipe({ name: 'lhTranslate', standalone: true, pure: false })
export class TranslatePipe implements PipeTransform, OnDestroy {
  private translationService = inject(TRANSLATE_SERVICE);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private lastKey: string | undefined;
  private lastVariant: string | undefined;
  private lastParams: Record<string, unknown> | undefined;
  private lastValue = '';
  private languageChangeSubscription: Subscription | undefined;

  public ngOnDestroy(): void {
    this.languageChangeSubscription?.unsubscribe();
  }

  /**
   * `params` fills a key's `{{tokens}}` (AM-4788: the free-cancellation policy hour). The shared
   * service has always accepted them; only this pipe dropped them on the floor, so a key with a
   * token rendered its literal braces to the traveler.
   */
  public transform(value: string, variant?: string, params?: Record<string, unknown>): string {
    // Subscribe to language changes on first use
    if (!this.languageChangeSubscription) {
      this.languageChangeSubscription = this.translationService.onLanguageChange.subscribe(() => {
        // Re-translate when language changes
        if (this.lastKey) {
          this.lastValue = this.translationService.instant(this.lastKey, this.lastVariant, this.lastParams);
          this.changeDetectorRef.markForCheck();
        }
      });
    }

    // Only re-translate if key, variant or params changed. Params are compared by VALUE: they are
    // built inline in templates, so a fresh object arrives on every change-detection pass and an
    // identity check would re-translate forever (this pipe is impure).
    //
    // A shallow key/value compare rather than JSON.stringify: stringify is sensitive to key order
    // (`{a,b}` and `{b,a}` are equal params but different strings, so it re-translates for nothing)
    // and it silently drops keys whose value is `undefined` (so `{time: undefined}` and `{}` compare
    // equal when one of them should render a token and the other should not). Both external review
    // lanes flagged it independently as a footgun for the next caller with a bigger params bag.
    const paramsChanged = !shallowEqual(params, this.lastParams);
    if (value !== this.lastKey || variant !== this.lastVariant || paramsChanged) {
      this.lastKey = value;
      this.lastVariant = variant;
      this.lastParams = params;
      this.lastValue = this.translationService.instant(value, variant, params);
    }

    return this.lastValue;
  }
}

/** Key/value equality one level deep — see the comparison in `transform`. */
function shallowEqual(a?: Record<string, unknown>, b?: Record<string, unknown>): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) {
    return false;
  }
  return ak.every((k) => Object.prototype.hasOwnProperty.call(b, k) && a[k] === b[k]);
}
